/**
 * Country Baseline Profiles
 * 
 * Defines tier and region for each country without hardcoded stats.
 * Tiers determine stat ranges, regions apply subtle modifiers.
 * 
 * Tiers (power level):
 * - superpower: Global influence, highest military/economy/tech
 * - major: Regional dominance, strong across all stats
 * - regional: Regional player, moderate stats
 * - minor: Limited power, lower stats
 * 
 * Regions (for subtle modifiers):
 * - europe, asia, americas, africa, middle_east, oceania
 */

export const COUNTRY_BASELINES = {
  // Superpowers (3)
  USA: { tier: 'superpower', region: 'americas' },
  CHN: { tier: 'superpower', region: 'asia' },
  RUS: { tier: 'superpower', region: 'europe' },

  // Major Powers (12)
  IND: { tier: 'major', region: 'asia' },
  JPN: { tier: 'major', region: 'asia' },
  DEU: { tier: 'major', region: 'europe' },
  GBR: { tier: 'major', region: 'europe' },
  FRA: { tier: 'major', region: 'europe' },
  BRA: { tier: 'major', region: 'americas' },
  CAN: { tier: 'major', region: 'americas' },
  AUS: { tier: 'major', region: 'oceania' },
  KOR: { tier: 'major', region: 'asia' },
  ITA: { tier: 'major', region: 'europe' },
  SAU: { tier: 'major', region: 'middle_east' },
  TUR: { tier: 'major', region: 'middle_east' },

  // Regional Powers (25)
  ESP: { tier: 'regional', region: 'europe' },
  MEX: { tier: 'regional', region: 'americas' },
  IDN: { tier: 'regional', region: 'asia' },
  POL: { tier: 'regional', region: 'europe' },
  NLD: { tier: 'regional', region: 'europe' },
  ARG: { tier: 'regional', region: 'americas' },
  EGY: { tier: 'regional', region: 'africa' },
  ZAF: { tier: 'regional', region: 'africa' },
  THA: { tier: 'regional', region: 'asia' },
  IRN: { tier: 'regional', region: 'middle_east' },
  PAK: { tier: 'regional', region: 'asia' },
  NGA: { tier: 'regional', region: 'africa' },
  VNM: { tier: 'regional', region: 'asia' },
  PHL: { tier: 'regional', region: 'asia' },
  MYS: { tier: 'regional', region: 'asia' },
  SGP: { tier: 'regional', region: 'asia' },
  ISR: { tier: 'regional', region: 'middle_east' },
  UKR: { tier: 'regional', region: 'europe' },
  SWE: { tier: 'regional', region: 'europe' },
  NOR: { tier: 'regional', region: 'europe' },
  BEL: { tier: 'regional', region: 'europe' },
  AUT: { tier: 'regional', region: 'europe' },
  CHE: { tier: 'regional', region: 'europe' },
  GRC: { tier: 'regional', region: 'europe' },
  CHL: { tier: 'regional', region: 'americas' },

  // Minor Powers (explicitly defined for special cases)
  GRL: { tier: 'minor', region: 'americas' }, // Greenland (autonomous territory of Denmark)

  // All other countries default to 'minor' tier
  // Region is inferred from geographic data
};

/**
 * Get baseline for a country, with fallback to minor tier
 */
export function getCountryBaseline(iso, fallbackRegion = 'africa') {
  if (COUNTRY_BASELINES[iso]) {
    return COUNTRY_BASELINES[iso];
  }
  
  // Default for unlisted countries
  return {
    tier: 'minor',
    region: fallbackRegion
  };
}

/**
 * Infer region from country ISO code (basic geographic grouping)
 * Used as fallback for countries not in COUNTRY_BASELINES
 */
export function inferRegion(iso) {
  // Europe
  const europe = ['ALB', 'AND', 'ARM', 'AZE', 'BLR', 'BIH', 'BGR', 'HRV', 'CYP', 
    'CZE', 'DNK', 'EST', 'FIN', 'GEO', 'HUN', 'ISL', 'IRL', 'XKX', 'LVA', 'LIE', 
    'LTU', 'LUX', 'MKD', 'MLT', 'MDA', 'MCO', 'MNE', 'PRT', 'ROU', 'SMR', 'SRB', 
    'SVK', 'SVN', 'VAT'];
  
  // Asia
  const asia = ['AFG', 'BGD', 'BTN', 'BRN', 'KHM', 'TWN', 'PRK', 'HKG', 'KAZ', 
    'KGZ', 'LAO', 'MAC', 'MDV', 'MNG', 'MMR', 'NPL', 'LKA', 'TJK', 'TLS', 'TKM', 
    'UZB'];
  
  // Americas
  const americas = ['ATG', 'BHS', 'BRB', 'BLZ', 'BOL', 'COL', 'CRI', 'CUB', 'DMA', 
    'DOM', 'ECU', 'SLV', 'GRD', 'GTM', 'GUY', 'HTI', 'HND', 'JAM', 'NIC', 'PAN', 
    'PRY', 'PER', 'KNA', 'LCA', 'VCT', 'SUR', 'TTO', 'URY', 'VEN'];
  
  // Middle East
  const middleEast = ['BHR', 'IRQ', 'JOR', 'KWT', 'LBN', 'OMN', 'PSE', 'QAT', 
    'SYR', 'ARE', 'YEM'];
  
  // Oceania
  const oceania = ['FJI', 'KIR', 'MHL', 'FSM', 'NRU', 'NZL', 'PLW', 'PNG', 'WSM', 
    'SLB', 'TON', 'TUV', 'VUT'];
  
  // Africa (default)
  if (europe.includes(iso)) return 'europe';
  if (asia.includes(iso)) return 'asia';
  if (americas.includes(iso)) return 'americas';
  if (middleEast.includes(iso)) return 'middle_east';
  if (oceania.includes(iso)) return 'oceania';
  
  return 'africa';
}
